import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import StatusPill from "@/components/StatusPill";
import { X, Loader2, ClipboardPlus } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable bulk-assign modal.
 * Props:
 *   employee: { employee_id, name } — required to open
 *   open: bool
 *   filterSkillCodes: string[] | undefined — when provided, only show skills in this list
 *   onClose(): closes without side effects
 *   onSuccess(counts): fired after successful confirm; counts = {created, skipped}
 */
export default function BulkAssignTniModal({ employee, open, onClose, onSuccess, filterSkillCodes }) {
  const [card, setCard] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (!open || !employee?.employee_id) return;
    setCard(null);
    setLoadErr("");
    api.get(`/competency-card/${employee.employee_id}`)
      .then((r) => setCard(r.data))
      .catch((e) => setLoadErr(e.response?.data?.detail || "Failed to load competency data"));
  }, [open, employee?.employee_id]);

  const pendingSkills = useMemo(() => {
    if (!card) return [];
    const filterSet = filterSkillCodes ? new Set(filterSkillCodes) : null;
    const seen = new Map();
    for (const r of card.roles || []) {
      for (const s of r.skills || []) {
        // When a FY-specific filter is provided, show ALL filtered skills regardless of status.
        // Without a filter (generic modal use), show only gap / not_assessed skills.
        const qualifies = filterSet
          ? filterSet.has(s.skill_code)
          : (s.status === "gap" || s.status === "not_assessed");

        if (qualifies) {
          if (!seen.has(s.skill_code)) {
            seen.set(s.skill_code, {
              skill_code: s.skill_code,
              skill_name: s.skill_name,
              required_level: s.required_level,
              status: s.status,
              roles: [r.role],
            });
          } else {
            seen.get(s.skill_code).roles.push(r.role);
          }
        }
      }
    }
    return Array.from(seen.values());
  }, [card, filterSkillCodes]);

  /** True when the employee's roles have zero skills defined in the competency map at all. */
  const hasNoRequirements = useMemo(() => {
    if (!card) return false;
    return (card.roles || []).every((r) => (r.skills || []).length === 0);
  }, [card]);

  const dueDate = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);

  const handleConfirm = async () => {
    if (pendingSkills.length === 0 || !employee) return;
    setAssigning(true);
    let created = 0;
    let skipped = 0;
    try {
      const results = await Promise.all(
        pendingSkills.map((sk) =>
          api.post("/assessments/assign", {
            employee_ids: [employee.employee_id],
            skill_code: sk.skill_code,
            type: "TNI",
            due_date: dueDate,
          }).then((r) => r.data).catch(() => null)
        )
      );
      for (const r of results) {
        if (!r) continue;
        created += (r.created || []).length;
        skipped += (r.skipped_pending || []).length
                + (r.skipped_recent || []).length
                + (r.skipped_inactive || []).length;
      }
      if (created > 0) {
        const tail = skipped > 0 ? ` (${skipped} skipped as duplicates)` : "";
        toast.success(`${created} TNI assessments assigned to ${employee.name}.${tail}`);
      } else {
        toast.info(`No new TNI assessments created — all pending skills already have active or recent tests.`);
      }
      onSuccess?.({ created, skipped });
      onClose();
    } catch {
      toast.error("Failed to assign one or more TNI tests. Please retry.");
    } finally {
      setAssigning(false);
    }
  };

  if (!open || !employee) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={() => !assigning && onClose()}
      data-testid="assign-all-tni-modal"
    >
      <div
        className="bg-white rounded-md max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-start justify-between">
          <div>
            <div className="bc-label">Bulk assignment</div>
            <h3 className="font-heading text-lg font-semibold mt-0.5">
              Assign Pending TNI Tests — {employee.name}
            </h3>
            <p className="text-xs text-[#666666] mt-1">
              {card === null && !loadErr && "Computing pending skills…"}
              {loadErr && <span className="text-[#C41E1E]">{loadErr}</span>}
              {card && (
                <>
                  {pendingSkills.length} skill{pendingSkills.length === 1 ? "" : "s"}{" "}
                  {filterSkillCodes
                    ? "missing FY assessment (no test assigned or completed this FY)"
                    : "currently in GAP or Not Assessed"}
                  . Due date: <span className="font-mono">{dueDate}</span>.
                  Duplicate tests are prevented automatically.
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => !assigning && onClose()}
            className="text-[#666666] hover:text-[#0D0D0D]"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {card && pendingSkills.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-[#F9F9F9] sticky top-0">
                <tr className="border-b border-[#E5E5E5]">
                  <th className="text-left px-5 py-2.5 bc-label">Skill</th>
                  <th className="text-left px-5 py-2.5 bc-label">Role(s)</th>
                  <th className="text-center px-5 py-2.5 bc-label">Required</th>
                  <th className="text-left px-5 py-2.5 bc-label">Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingSkills.map((s) => (
                  <tr
                    key={s.skill_code}
                    className="border-b border-[#E5E5E5]"
                    data-testid={`assign-all-tni-row-${s.skill_code}`}
                  >
                    <td className="px-5 py-2">{s.skill_name}</td>
                    <td className="px-5 py-2 text-xs text-[#666666]">{s.roles.join(", ")}</td>
                    <td className="px-5 py-2 text-center font-mono">{s.required_level}</td>
                    <td className="px-5 py-2">
                      {s.status === "gap" ? (
                        <span className="inline-flex items-center gap-1 text-[#C41E1E] text-xs font-medium">
                          <X size={12} /> GAP
                        </span>
                      ) : s.status === "competent" && filterSkillCodes ? (
                        <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-medium">
                          FY re-assessment due
                        </span>
                      ) : (
                        <StatusPill status="not_assessed" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {card && pendingSkills.length === 0 && (
            <div className="px-5 py-8 text-sm text-[#666666] text-center" data-testid="bulk-assign-empty">
              {hasNoRequirements
                ? "This employee's role has no competency requirements defined."
                : "No pending competencies — this employee is fully assessed."}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[#E5E5E5] flex items-center justify-end gap-2 bg-[#FAFAFA]">
          <button
            type="button"
            onClick={onClose}
            disabled={assigning}
            data-testid="assign-all-tni-cancel"
            className="text-sm px-4 py-2 rounded-sm border border-[#D4D4D4] hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={assigning || !card || pendingSkills.length === 0}
            data-testid="assign-all-tni-confirm"
            className="inline-flex items-center gap-1.5 bg-[#C41E1E] hover:bg-[#a71919] text-white text-sm rounded-sm px-4 py-2 disabled:opacity-50"
          >
            {assigning ? <Loader2 size={14} className="animate-spin" /> : <ClipboardPlus size={14} />}
            {assigning
              ? "Assigning…"
              : pendingSkills.length > 0
                ? `Assign ${pendingSkills.length} Test${pendingSkills.length === 1 ? "" : "s"}`
                : "Nothing to Assign"}
          </button>
        </div>
      </div>
    </div>
  );
}
